// ReposPage.tsx — every repository docdaemon watches, one card each, with the
// add-a-repository form underneath for a session that may write.
//
// Adding sits below the listing rather than above it for the same reason
// imprinter's course authoring does: the page is read far more often than it
// is written to, and the thing you came for should not be one scroll below a
// form. POST /api/repos enforces the same admin scope this hides the form on,
// so hiding it hides something the API would refuse anyway.
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { BsPlusLg } from 'react-icons/bs';
import Text from 'common/ui/Text';
import type { AuthState } from 'common';
import { addRepo, fetchRepos, isAdmin } from '../api';
import type { Repo } from '../api';
import RepoCard from '../components/RepoCard';

export default function ReposPage({ auth }: { auth: AuthState }) {
  const [repos, setRepos] = useState<Repo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fullName, setFullName] = useState('');
  const [adding, setAdding] = useState(false);
  const admin = isAdmin(auth);

  const load = async () => {
    try {
      const list = await fetchRepos();
      // Alphabetical, always: the cards carry their own status, and a grid
      // that re-sorts itself as runs start and finish makes the card under
      // the pointer move out from under it.
      setRepos([...list].sort((a, b) => a.full_name.localeCompare(b.full_name)));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load repositories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setError('');
    try {
      await addRepo(fullName.trim());
      setFullName('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add repository');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div>
      <h1 className="mb-3">Repositories</h1>
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>
      ) : repos.length === 0 ? (
        <p className="text-muted text-center py-4">
          {admin
            ? 'No repositories watched yet. Add one below, or wait for auto-discovery to find them.'
            : 'No repositories watched yet.'}
        </p>
      ) : (
        <div className="card-grid mb-4">
          {repos.map((repo) => (
            <RepoCard key={repo.id} repo={repo} admin={admin} onChanged={() => void load()} />
          ))}
        </div>
      )}

      {admin && (
        <Card>
          <Card.Header>Add Repository</Card.Header>
          <Card.Body>
            <Form id="add-repo-form" onSubmit={add}>
              <Form.Group>
                <Form.Label>Repository</Form.Label>
                <Text
                  type="text"
                  style={{ width: '100%' }}
                  value={fullName}
                  required
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="owner/name"
                />
                {/* as="div" because Text is an inline-block that sizes itself
                    to its contents; the default inline <small> wraps around it
                    instead of sitting under it. */}
                <Form.Text as="div" muted>
                  The full name as gitea spells it. docdaemon clones it, walks it, and
                  documents the whole tree on the first run — a large repository is a
                  large bill, so it is worth knowing which one you are naming.
                </Form.Text>
              </Form.Group>
            </Form>
          </Card.Body>
          <Card.Footer>
            <Button
              variant="primary"
              type="submit"
              form="add-repo-form"
              disabled={adding || !fullName.trim()}
              className="d-flex align-items-center gap-2"
            >
              <BsPlusLg /> Add Repository
            </Button>
          </Card.Footer>
        </Card>
      )}
    </div>
  );
}
